import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { http } from '@monorepo/infra'
import { PageSpinner } from '@monorepo/components'
import { ProductDownloadBreakdownDto } from '@monorepo/interfaces'
import { ResponsiveBar } from '@nivo/bar'
import { ResponsivePie } from '@nivo/pie'
import { format } from 'date-fns'
import * as FileSaver from 'file-saver'
import XLSX from 'sheetjs-style'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import * as S from './styles'

/**
 * Leadership snapshot shown above "Create a report" on Reports & Analytics.
 *
 * Served pre-aggregated by GET /reports/dashboard, which returns each KPI as
 * {current, previous, lastYear} so the cards can show MoM and YoY.
 */

/**
 * A KPI across the comparison windows.
 *
 * `null` means the query FAILED — not zero. Showing 0 for a broken query would
 * put a fabricated business number in front of leadership, so nulls render as
 * "—  data unavailable".
 */
type Metric = {
  current: number | null
  previous: number | null
  lastYear: number | null
}

type SpendRow = { category: string; creditCard: number; coop: number }

/**
 * How often one asset family was ordered in one channel.
 *
 * Counted per asset, so two sales sheets on one order count as two — the unit
 * Amanda works in. `downloaded` and `printed` are both returned so the chart can
 * show either without a new query.
 */
type AssetCategoryRow = {
  category: string
  channel: string
  downloaded: number
  printed: number
  total: number
}

type TopDownloadRow = {
  id: string
  name: string
  total: number
  byChannel: Record<string, number>
}

interface DashboardData {
  period: {
    from: string
    to: string
    previousFrom: string
    previousTo: string
    lastYearFrom: string
    lastYearTo: string
    channel: string | null
  }
  kpis: {
    activeUsers: Metric
    orders: Metric
    downloads: Metric
  }
  commerce: {
    adMaterialsPrinted: Metric
    adMaterialsDownloaded: Metric
    media: Metric
    microsites: Metric
    events: Metric
  }
  traffic: {
    merchandise: Metric
    resources: Metric
    demo: Metric
  }
  // null (not []) means the query failed — distinct from "genuinely no data"
  spend: SpendRow[] | null
  assetCategories: AssetCategoryRow[] | null
  byChannel: { channel: string; activeUsers: number; activity: number }[] | null
  engagement: { itemType: number; label: string; count: number }[] | null
  /** Sections whose query failed; render these as unavailable, never as zero. */
  unavailable: string[]
}

const iso = (d: Date) => format(d, 'yyyy-MM-dd')

const UNAVAILABLE: Metric = { current: null, previous: null, lastYear: null }

/**
 * Fill in any section the API didn't return.
 *
 * Frontend and backend deploy independently, so a newer UI can talk to an
 * older API. Missing sections become "unavailable" (nulls) rather than
 * crashing on undefined — and never render as a fabricated zero.
 */
function normalize(payload: DashboardData): DashboardData {
  return {
    ...payload,
    kpis: {
      activeUsers: payload.kpis?.activeUsers ?? UNAVAILABLE,
      orders: payload.kpis?.orders ?? UNAVAILABLE,
      downloads: payload.kpis?.downloads ?? UNAVAILABLE,
    },
    commerce: {
      adMaterialsPrinted: payload.commerce?.adMaterialsPrinted ?? UNAVAILABLE,
      adMaterialsDownloaded:
        payload.commerce?.adMaterialsDownloaded ?? UNAVAILABLE,
      media: payload.commerce?.media ?? UNAVAILABLE,
      microsites: payload.commerce?.microsites ?? UNAVAILABLE,
      events: payload.commerce?.events ?? UNAVAILABLE,
    },
    traffic: {
      merchandise: payload.traffic?.merchandise ?? UNAVAILABLE,
      resources: payload.traffic?.resources ?? UNAVAILABLE,
      demo: payload.traffic?.demo ?? UNAVAILABLE,
    },
    spend: payload.spend ?? null,
    assetCategories: payload.assetCategories ?? null,
    byChannel: payload.byChannel ?? null,
    engagement: payload.engagement ?? null,
    unavailable: payload.unavailable ?? [],
  }
}

/** Default view is the last 30 days. */
function defaultRange(): { dateFrom: string; dateTo: string } {
  const now = new Date()
  const from = new Date(now)
  from.setDate(from.getDate() - 29)
  return { dateFrom: iso(from), dateTo: iso(now) }
}

/**
 * Axis ticks forced to whole numbers.
 *
 * Users and assets are countable things — nivo's default scale invents 0.5 /
 * 1.5 ticks when the values are small integers, which reads as "half a user".
 */
function integerTicks(max: number): number[] {
  const top = Math.max(1, Math.ceil(max))
  const step = Math.max(1, Math.ceil(top / 5))
  const ticks: number[] = []
  for (let v = 0; v <= top; v += step) ticks.push(v)
  if (ticks[ticks.length - 1] !== top) ticks.push(top)
  return ticks
}

const usd = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD' })

/**
 * Asset families, taken from the response rather than hardcoded.
 *
 * The API already returns every configured family — including ones with no
 * orders — in the order they are defined, so deriving the list keeps a single
 * source of truth. Hardcoding the names here would mean a rename on the backend
 * silently rendered three empty charts instead of an obvious failure.
 */
function assetFamiliesFrom(rows: AssetCategoryRow[] | null): string[] {
  return Array.from(new Set((rows || []).map((r) => r.category)))
}

const CHANNEL_ORDER = ['LSP', 'MDU', 'D2D', 'ISP', 'EM', 'ND']

const CHANNEL_COLORS: Record<string, string> = {
  LSP: '#1f5d75',
  MDU: '#e56b2f',
  D2D: '#2e8b57',
  ISP: '#169bd5',
  EM: '#9b2fae',
  ND: '#74a92f',
}

/**
 * The production Digital Downloads report exposes one activity row per
 * downloaded asset. Group those rows by SKU and channel, then keep the ten
 * most-downloaded assets for Amanda's dashboard chart.
 */
function topDownloadsFrom(
  payload: ProductDownloadBreakdownDto
): TopDownloadRow[] {
  const grouped = new Map<string, TopDownloadRow>()
  const productNames = new Map<string, string>()

  for (const product of payload.products || []) {
    if (product.sku && product.name) {
      productNames.set(product.sku.trim(), product.name.trim())
    }
  }

  for (const row of payload.activityByDealer || []) {
    const name =
      (row.sku ? productNames.get(row.sku.trim()) : undefined) ||
      row.name?.trim() ||
      row.sku?.trim() ||
      'Unnamed asset'
    const id = row.sku?.trim() || name
    const channel = row.channel?.trim().toUpperCase() || 'OTHER'
    if (!CHANNEL_ORDER.includes(channel)) continue
    const existing = grouped.get(id) || {
      id,
      name,
      total: 0,
      byChannel: {},
    }

    existing.total += 1
    existing.byChannel[channel] = (existing.byChannel[channel] || 0) + 1
    grouped.set(id, existing)
  }

  return Array.from(grouped.values())
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name))
    .slice(0, 10)
}

/**
 * Display copy lives here, not in the API response.
 *
 * Wording gets revised far more often than logic, so keeping it client-side
 * means a copy change ships with the frontend instead of needing a backend
 * deploy.
 */
const COPY = {
  activeUsers: 'Unique users with at least one tracked action in the period.',
  orders: 'Orders placed in the period, excluding deleted orders.',
  downloads:
    'Every downloadable asset ordered, counted individually rather than per order. Free assets are included.',
  adMaterials:
    'The number of assets purchased for print and ship and assets downloaded.',
  media: 'Completed orders with Media items priced above $0.',
  microsites: 'Completed orders with Microsites priced above $0.',
  events: 'Completed orders with Events priced above $0.',
  merchandise:
    'Clicks on the Merchandise tab. Purchases cannot be measured without a data feed from Dialog Direct.',
  resources: 'Clicks into the Resources area.',
  demo: 'Clicks on the DIRECTV Demo link.',
  assetCategories:
    'How often each asset family was ordered, by channel, counted per asset. Assets are identified by name, so anything not named to convention is not included.',
  topDownloads:
    'The ten most-downloaded assets in the period, broken down by channel.',
  engagement: 'Clicks into each area of the site.*',
  // Dated rather than "recently": this dashboard will be used for years, and
  // anyone pulling a year-over-year comparison needs to know why an earlier
  // range shows no merchandise at all.
  engagementFootnote: '* Merchandise tracking starting 8/12/26',
  spend:
    'Dealer spend per category, split into credit card and Co-Op. Where one order mixes categories, its payments are allocated across items in proportion to line value.',
  byChannel:
    'Users may have access to multiple channels. Channel totals reflect activity by channel and do not represent unique users.',
}

/** Percent change; null when unavailable or the baseline is zero. */
function pctChange(
  current: number | null,
  baseline: number | null
): number | null {
  if (current === null || baseline === null) return null
  if (baseline === 0) return current === 0 ? 0 : null
  return ((current - baseline) / baseline) * 100
}

const DeltaBadge: React.FC<{
  current: number | null
  baseline: number | null
  caption: string
}> = ({ current, baseline, caption }) => {
  const pct = pctChange(current, baseline)
  if (pct === null) {
    return (
      <S.Delta direction="flat">
        —&nbsp;<S.DeltaCaption>{caption}</S.DeltaCaption>
      </S.Delta>
    )
  }
  const direction = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat'
  const arrow = pct > 0 ? '▲' : pct < 0 ? '▼' : '—'
  return (
    <S.Delta direction={direction}>
      {arrow} {Math.abs(Math.round(pct))}%&nbsp;
      <S.DeltaCaption>{caption}</S.DeltaCaption>
    </S.Delta>
  )
}

const StatCard: React.FC<{ label: string; hint: string; metric: Metric }> = ({
  label,
  hint,
  metric,
}) => {
  // A failed query must never be drawn as 0 — show it as unavailable instead.
  if (metric.current === null) {
    return (
      <S.StatCard>
        <S.StatLabel>{label}</S.StatLabel>
        <S.StatValue>—</S.StatValue>
        <S.DeltaRow>
          <S.DeltaCaption>data unavailable</S.DeltaCaption>
        </S.DeltaRow>
      </S.StatCard>
    )
  }
  return (
    <S.StatCard>
      <S.StatLabel>{label}</S.StatLabel>
      <S.StatValue>{metric.current.toLocaleString()}</S.StatValue>
      <S.DeltaRow>
        <DeltaBadge
          current={metric.current}
          baseline={metric.previous}
          caption="MoM"
        />
        <DeltaBadge
          current={metric.current}
          baseline={metric.lastYear}
          caption="YoY"
        />
      </S.DeltaRow>
      <S.StatHint>{hint}</S.StatHint>
    </S.StatCard>
  )
}

/** Ad Materials: one tile, two numbers, as Amanda asked for. */
const SplitStatCard: React.FC<{
  label: string
  hint: string
  left: { label: string; metric: Metric }
  right: { label: string; metric: Metric }
}> = ({ label, hint, left, right }) => (
  <S.SplitStatCard>
    <S.StatLabel>{label}</S.StatLabel>
    <S.SplitRow>
      {[left, right].map((side) => (
        <S.SplitItem key={side.label}>
          <S.SplitValue>
            {side.metric.current === null
              ? '—'
              : side.metric.current.toLocaleString()}
          </S.SplitValue>
          <S.SplitLabel>{side.label}</S.SplitLabel>
          {side.metric.current !== null && (
            <S.DeltaRow>
              <DeltaBadge
                current={side.metric.current}
                baseline={side.metric.previous}
                caption="MoM"
              />
            </S.DeltaRow>
          )}
        </S.SplitItem>
      ))}
    </S.SplitRow>
    <S.StatHint>{hint}</S.StatHint>
  </S.SplitStatCard>
)

const CHART_THEME = {
  fontFamily: 'PFDINText',
  fontSize: 11,
  axis: { ticks: { text: { fill: '#777777' } } },
  grid: { line: { stroke: '#eeeeee' } },
}

const SectionDashboard: React.FC = () => {
  const initial = useMemo(defaultRange, [])
  // Applied range drives the fetch; the inputs are edited freely until Apply.
  const [range, setRange] = useState(initial)
  const [draft, setDraft] = useState(initial)
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [topDownloads, setTopDownloads] = useState<TopDownloadRow[] | null>(
    null
  )
  const [topDownloadsLoading, setTopDownloadsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pdfBusy, setPdfBusy] = useState(false)

  // One ref per section, so the PDF export can rasterise each independently and
  // put it on its own landscape page.
  const activeUsersRef = useRef<HTMLDivElement>(null)
  const ordersRef = useRef<HTMLDivElement>(null)
  const engagementRef = useRef<HTMLDivElement>(null)

  const load = useCallback(() => {
    setLoading(true)
    setTopDownloadsLoading(true)
    setError(null)

    http
      .get<DashboardData>({
        url: '/reports/dashboard',
        params: { dateFrom: range.dateFrom, dateTo: range.dateTo },
      })
      .then(({ data: payload }) => setData(normalize(payload)))
      .catch(() => setError('Unable to load the dashboard right now.'))
      .finally(() => setLoading(false))

    http
      .get<ProductDownloadBreakdownDto>({
        url: '/reports/productDownloadBreakdown',
        params: {
          channel: 'all',
          dateFrom: range.dateFrom,
          dateTo: range.dateTo,
        },
      })
      .then(({ data: payload }) => setTopDownloads(topDownloadsFrom(payload)))
      .catch(() => setTopDownloads(null))
      .finally(() => setTopDownloadsLoading(false))
  }, [range.dateFrom, range.dateTo])

  useEffect(() => {
    load()
  }, [load])

  /**
   * Excel export of the numbers behind the dashboard.
   *
   * She wants a button that hands over the data behind the high-level view, so
   * each block becomes its own sheet.
   */
  const exportExcel = useCallback(() => {
    if (!data) return
    const metricRow = (name: string, m: Metric) => ({
      Metric: name,
      Current: m.current ?? 'unavailable',
      'Previous period': m.previous ?? 'unavailable',
      'Same period last year': m.lastYear ?? 'unavailable',
    })

    const summary = [
      metricRow('Active Users', data.kpis.activeUsers),
      metricRow('Orders', data.kpis.orders),
      metricRow('Digital Downloads', data.kpis.downloads),
      metricRow(
        'Ad Materials - print & ship',
        data.commerce.adMaterialsPrinted
      ),
      metricRow(
        'Ad Materials - downloads',
        data.commerce.adMaterialsDownloaded
      ),
      metricRow('Media', data.commerce.media),
      metricRow('Microsites', data.commerce.microsites),
      metricRow('Events', data.commerce.events),
      metricRow('Merchandise traffic', data.traffic.merchandise),
      metricRow('Resource traffic', data.traffic.resources),
      metricRow('DIRECTV Demo traffic', data.traffic.demo),
    ]

    const spend = (data.spend || []).map((r) => ({
      Category: r.category,
      'Credit card': r.creditCard,
      'Co-Op': r.coop,
      Total: round2(r.creditCard + r.coop),
    }))

    const channels = (data.byChannel || []).map((c) => ({
      Channel: c.channel,
      'Active users': c.activeUsers,
      Activity: c.activity,
    }))

    const engagement = (data.engagement || []).map((e) => ({
      Area: e.label,
      Clicks: e.count,
    }))

    const wb = { Sheets: {}, SheetNames: [] } as unknown as XLSX.WorkBook
    const add = (name: string, rows: Record<string, unknown>[]) => {
      if (!rows.length) return
      wb.Sheets[name] = XLSX.utils.json_to_sheet(rows)
      wb.SheetNames.push(name)
    }
    // Both delivery routes, since her manual figures come from downloads while
    // the ask was phrased as "processed in an order".
    // Zero rows are placeholders the API sends so a family with no orders still
    // renders a chart — they would just be noise in a spreadsheet.
    const assetFamilies = (data.assetCategories || [])
      .filter((r) => r.total > 0)
      .map((r) => ({
        'Asset family': r.category,
        Channel: r.channel,
        Downloaded: r.downloaded,
        'Print & ship': r.printed,
        Total: r.total,
      }))

    const topDownloadRows = (topDownloads || []).map((r) => ({
      Asset: r.name,
      Total: r.total,
      ...r.byChannel,
    }))

    add('Summary', summary)
    add('Top Downloads', topDownloadRows)
    add('Asset Families', assetFamilies)
    add('Spend', spend)
    add('By Channel', channels)
    add('Engagement', engagement)

    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    FileSaver.saveAs(
      new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset-UTF-8',
      }),
      `TMZ-Dashboard-${range.dateFrom}_to_${range.dateTo}.xlsx`
    )
  }, [data, range.dateFrom, range.dateTo, topDownloads])

  /**
   * PDF export — one section per landscape page (Active Users, Orders, Dealer
   * Engagement), matching the on-screen sections. Each section div is
   * rasterised with html2canvas and placed on its own page, scaled to fit.
   *
   * Amanda shares this as a PDF (like the demo app export), so the charts must
   * carry their numbers without hovering — the bar charts already show data
   * labels and the donut shows a legend plus per-slice values.
   */
  const exportPdf = useCallback(async () => {
    const sections: { ref: React.RefObject<HTMLDivElement>; title: string }[] = [
      { ref: activeUsersRef, title: 'Active Users' },
      { ref: ordersRef, title: 'Orders' },
      { ref: engagementRef, title: 'Dealer Engagement' },
    ]
    setPdfBusy(true)
    try {
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' })
      const pageW = pdf.internal.pageSize.getWidth()
      const pageH = pdf.internal.pageSize.getHeight()
      const margin = 32
      const titleGap = 22
      let firstPageDrawn = false

      for (const section of sections) {
        const el = section.ref.current
        if (!el) continue
        const canvas = await html2canvas(el, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          scrollX: 0,
          scrollY: 0,
        })
        if (firstPageDrawn) pdf.addPage('letter', 'landscape')
        firstPageDrawn = true

        pdf.setFontSize(15)
        pdf.setTextColor(11, 44, 77)
        pdf.text(
          `${section.title}  —  ${range.dateFrom} to ${range.dateTo}`,
          margin,
          margin
        )

        const availW = pageW - margin * 2
        const availH = pageH - margin * 2 - titleGap
        const ratio = Math.min(availW / canvas.width, availH / canvas.height)
        const imgW = canvas.width * ratio
        const imgH = canvas.height * ratio
        pdf.addImage(
          canvas.toDataURL('image/png'),
          'PNG',
          margin,
          margin + titleGap,
          imgW,
          imgH
        )
      }

      if (!firstPageDrawn) return
      pdf.save(`TMZ-Dashboard-${range.dateFrom}_to_${range.dateTo}.pdf`)
    } finally {
      setPdfBusy(false)
    }
  }, [range.dateFrom, range.dateTo])

  if (loading && !data) return <PageSpinner />

  if (error) {
    return (
      <S.Container>
        <S.EmptyState>{error}</S.EmptyState>
      </S.Container>
    )
  }

  if (!data) return null

  const {
    kpis,
    commerce,
    spend,
    assetCategories,
    byChannel,
    engagement,
  } = data
  const rangeChanged =
    draft.dateFrom !== range.dateFrom || draft.dateTo !== range.dateTo
  const dashboardBusy = loading || topDownloadsLoading
  const topDownloadChannels = Array.from(
    new Set(
      (topDownloads || []).reduce<string[]>(
        (channels, row) => channels.concat(Object.keys(row.byChannel)),
        []
      )
    )
  ).sort((a, b) => {
    const aIndex = CHANNEL_ORDER.indexOf(a)
    const bIndex = CHANNEL_ORDER.indexOf(b)
    if (aIndex === -1 && bIndex === -1) return a.localeCompare(b)
    if (aIndex === -1) return 1
    if (bIndex === -1) return -1
    return aIndex - bIndex
  })
  const topDownloadNames = (topDownloads || []).reduce<Record<string, string>>(
    (names, row) => ({ ...names, [row.id]: row.name }),
    {}
  )
  const topDownloadChartData = (topDownloads || []).map((row) => ({
    assetId: row.id,
    ...row.byChannel,
  }))
  const topDownloadMax = Math.max(
    1,
    ...(topDownloads || []).reduce<number[]>(
      (values, row) => values.concat(Object.values(row.byChannel)),
      []
    )
  )

  return (
    <S.Container>
      <S.HeaderRow>
        <S.DateRangeRow>
          <S.DateField>
            <S.DateLabel>From</S.DateLabel>
            <S.DateInput
              type="date"
              value={draft.dateFrom}
              max={draft.dateTo}
              onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value })}
            />
          </S.DateField>
          <S.DateField>
            <S.DateLabel>To</S.DateLabel>
            <S.DateInput
              type="date"
              value={draft.dateTo}
              min={draft.dateFrom}
              onChange={(e) => setDraft({ ...draft, dateTo: e.target.value })}
            />
          </S.DateField>
          <S.PresetButton
            active={rangeChanged && !dashboardBusy}
            disabled={!rangeChanged || dashboardBusy}
            onClick={() => setRange(draft)}
            aria-label={dashboardBusy ? 'Loading dashboard' : 'Apply date range'}
            aria-busy={dashboardBusy}
          >
            {dashboardBusy ? <S.ButtonSpinner /> : 'Apply'}
          </S.PresetButton>
        </S.DateRangeRow>
        <S.Controls>
          <S.ExportButton
            onClick={exportPdf}
            disabled={pdfBusy || dashboardBusy}
            aria-busy={pdfBusy}
          >
            {pdfBusy ? (
              <>
                <S.ButtonSpinner /> Preparing PDF…
              </>
            ) : (
              'Export to PDF'
            )}
          </S.ExportButton>
          <S.ExportButton onClick={exportExcel} disabled={dashboardBusy}>
            Export to Excel
          </S.ExportButton>
        </S.Controls>
      </S.HeaderRow>

      {data.unavailable && data.unavailable.length > 0 && (
        <S.Warning>
          Some data couldn&rsquo;t be loaded ({data.unavailable.join(', ')}).
          Those figures are shown as &ldquo;—&rdquo; rather than zero — treat
          this snapshot as incomplete.
        </S.Warning>
      )}

      {/* ── Section 1: Active Users ─────────────────────────────── */}
      <S.SectionBlock ref={activeUsersRef}>
        <S.GroupTitle>Active Users</S.GroupTitle>
        <S.StatRowThree>
          <StatCard
            label="Active Users"
            hint={COPY.activeUsers}
            metric={kpis.activeUsers}
          />
        </S.StatRowThree>

        <S.ChartCard>
          <S.ChartTitle>Active Users by Channel</S.ChartTitle>
          <S.ChartSubtitle>{COPY.byChannel}</S.ChartSubtitle>
          <S.ChartBody height={260}>
            {byChannel === null ? (
              <S.EmptyState>Couldn&rsquo;t load this chart</S.EmptyState>
            ) : byChannel.length === 0 ? (
              <S.EmptyState>No channel activity in this period</S.EmptyState>
            ) : (
              <ResponsiveBar
                data={byChannel.map((c) => ({
                  channel: c.channel,
                  activeUsers: c.activeUsers,
                }))}
                keys={['activeUsers']}
                indexBy="channel"
                layout="horizontal"
                margin={{ top: 10, right: 30, bottom: 30, left: 70 }}
                padding={0.3}
                colors={['#76D4F8']}
                labelSkipWidth={16}
                axisBottom={{
                  tickValues: integerTicks(
                    Math.max(...byChannel.map((c) => c.activeUsers), 1)
                  ),
                }}
                gridXValues={integerTicks(
                  Math.max(...byChannel.map((c) => c.activeUsers), 1)
                )}
                theme={CHART_THEME}
              />
            )}
          </S.ChartBody>
        </S.ChartCard>
      </S.SectionBlock>

      {/* ── Section 2: Orders ───────────────────────────────────── */}
      <S.SectionBlock ref={ordersRef}>
        <S.GroupTitle>Orders</S.GroupTitle>
        <S.StatRow>
          <SplitStatCard
            label="Ad Materials"
            hint={COPY.adMaterials}
            left={{
              label: 'Print & ship',
              metric: commerce.adMaterialsPrinted,
            }}
            right={{
              label: 'Downloads',
              metric: commerce.adMaterialsDownloaded,
            }}
          />
          <StatCard label="Media" hint={COPY.media} metric={commerce.media} />
          <StatCard
            label="Microsites"
            hint={COPY.microsites}
            metric={commerce.microsites}
          />
          <StatCard label="Events" hint={COPY.events} metric={commerce.events} />
        </S.StatRow>

        <S.ChartCard>
          <S.ChartTitle>Dealer Spend — Credit Card vs Co-Op</S.ChartTitle>
          <S.ChartSubtitle>{COPY.spend}</S.ChartSubtitle>
          <S.ChartBody height={Math.max(240, (spend?.length || 1) * 46 + 60)}>
            {spend === null ? (
              <S.EmptyState>Couldn&rsquo;t load this chart</S.EmptyState>
            ) : spend.length === 0 ? (
              <S.EmptyState>No dealer spend in this period</S.EmptyState>
            ) : (
              <ResponsiveBar
                data={spend.map((r) => ({
                  category: r.category,
                  'Credit card': r.creditCard,
                  'Co-Op': r.coop,
                }))}
                keys={['Credit card', 'Co-Op']}
                indexBy="category"
                layout="horizontal"
                margin={{ top: 10, right: 30, bottom: 45, left: 150 }}
                padding={0.3}
                colors={['#337ace', '#8CC63F']}
                valueFormat={(v) => usd(Number(v))}
                labelSkipWidth={48}
                labelTextColor={(bar) =>
                  String(bar.data.id) === 'Credit card' ? '#ffffff' : '#111111'
                }
                legends={[
                  {
                    dataFrom: 'keys',
                    anchor: 'bottom',
                    direction: 'row',
                    translateY: 42,
                    itemWidth: 100,
                    itemHeight: 16,
                    symbolSize: 12,
                  },
                ]}
                theme={CHART_THEME}
              />
            )}
          </S.ChartBody>
        </S.ChartCard>
      </S.SectionBlock>

      {/* ── Section 3: Dealer Engagement ────────────────────────── */}
      <S.SectionBlock ref={engagementRef}>
        <S.GroupTitle>Dealer Engagement</S.GroupTitle>

        <S.ChartGrid>
          <S.ChartCard>
            <S.ChartTitle>Engagement Mix</S.ChartTitle>
            <S.ChartSubtitle>{COPY.engagement}</S.ChartSubtitle>
            <S.ChartBody height={400}>
              {engagement === null ? (
                <S.EmptyState>Couldn&rsquo;t load this chart</S.EmptyState>
              ) : engagement.length === 0 ? (
                <S.EmptyState>No engagement recorded</S.EmptyState>
              ) : (
                <ResponsivePie
                  data={engagement.map((e) => ({
                    id: e.label,
                    label: e.label,
                    value: e.count,
                  }))}
                  margin={{ top: 24, right: 120, bottom: 24, left: 60 }}
                  // Rotate the full circle just enough to put the final,
                  // smallest slice (Merchandise) on the open top-right side.
                  // Keeping a 360-degree span avoids creating a visible gap.
                  startAngle={6}
                  endAngle={366}
                  innerRadius={0.55}
                  padAngle={1}
                  cornerRadius={2}
                  colors={[
                    '#337ace',
                    '#76d4f8',
                    '#213c56',
                    '#8cc63f',
                    '#f2a900',
                    '#9b2fae',
                  ]}
                  // A number on every slice, including tiny ones, on lines
                  // pointing outward — so they survive a PDF export where
                  // labels inside small arcs disappear.
                  enableArcLinkLabels={true}
                  arcLinkLabel={(d) => String(d.value)}
                  arcLinkLabelsSkipAngle={0}
                  arcLinkLabelsDiagonalLength={14}
                  arcLinkLabelsStraightLength={16}
                  arcLinkLabelsTextColor="#333333"
                  arcLinkLabelsThickness={1}
                  enableArcLabels={false}
                  legends={[
                    {
                      anchor: 'right',
                      direction: 'column',
                      translateX: 112,
                      itemWidth: 106,
                      itemHeight: 20,
                      symbolSize: 12,
                      symbolShape: 'circle',
                    },
                  ]}
                  theme={CHART_THEME}
                />
              )}
            </S.ChartBody>
            <S.ChartFootnote>{COPY.engagementFootnote}</S.ChartFootnote>
          </S.ChartCard>

          <S.ChartCard>
            <S.ChartTitle>Top 10 Downloads</S.ChartTitle>
            <S.ChartSubtitle>{COPY.topDownloads}</S.ChartSubtitle>
            <S.ChartBody height={400}>
              {topDownloadsLoading ? (
                <S.EmptyState>Loading top downloads…</S.EmptyState>
              ) : topDownloads === null ? (
                <S.EmptyState>Couldn&rsquo;t load this chart</S.EmptyState>
              ) : topDownloads.length === 0 ? (
                <S.EmptyState>No downloads in this period</S.EmptyState>
              ) : (
                <ResponsiveBar
                  data={topDownloadChartData}
                  keys={topDownloadChannels}
                  indexBy="assetId"
                  layout="horizontal"
                  groupMode="grouped"
                  margin={{ top: 8, right: 24, bottom: 52, left: 220 }}
                  padding={0.18}
                  innerPadding={1}
                  colors={(bar) =>
                    CHANNEL_COLORS[String(bar.id)] || '#6f7c87'
                  }
                  label={(bar) =>
                    Number(bar.value) > 0 ? String(bar.value) : ''
                  }
                  labelSkipWidth={18}
                  labelSkipHeight={9}
                  labelTextColor="#ffffff"
                  axisLeft={{
                    format: (id) => topDownloadNames[String(id)] || String(id),
                  }}
                  axisBottom={{ tickValues: integerTicks(topDownloadMax) }}
                  gridXValues={integerTicks(topDownloadMax)}
                  legends={[
                    {
                      dataFrom: 'keys',
                      anchor: 'bottom',
                      direction: 'row',
                      translateY: 46,
                      itemWidth: 58,
                      itemHeight: 16,
                      symbolSize: 11,
                    },
                  ]}
                  theme={CHART_THEME}
                />
              )}
            </S.ChartBody>
          </S.ChartCard>
        </S.ChartGrid>

        <S.ChartSubtitle>{COPY.assetCategories}</S.ChartSubtitle>
        {assetCategories === null ? (
          <S.ChartCard>
            <S.EmptyState>Couldn&rsquo;t load asset family data</S.EmptyState>
          </S.ChartCard>
        ) : (
          <S.StatRowThree>
            {assetFamiliesFrom(assetCategories).map((family) => {
              const rows = (assetCategories || [])
                .filter((r) => r.category === family && r.total > 0)
                .sort((a, b) => b.total - a.total)
              const familyTotal = rows.reduce((s, r) => s + r.total, 0)
              const max = Math.max(...rows.map((r) => r.total), 1)
              return (
                <S.ChartCard key={family}>
                  <S.ChartTitle>{family}</S.ChartTitle>
                  <S.ChartSubtitle>
                    {familyTotal.toLocaleString()} ordered in this period
                  </S.ChartSubtitle>
                  <S.ChartBody height={220}>
                    {rows.length === 0 ? (
                      <S.EmptyState>None ordered in this period</S.EmptyState>
                    ) : (
                      <ResponsiveBar
                        data={rows.map((r) => ({
                          channel: r.channel,
                          ordered: r.total,
                        }))}
                        keys={['ordered']}
                        indexBy="channel"
                        layout="horizontal"
                        margin={{ top: 8, right: 26, bottom: 28, left: 52 }}
                        padding={0.3}
                        colors={['#337ace']}
                        labelSkipWidth={16}
                        labelTextColor="#ffffff"
                        axisBottom={{ tickValues: integerTicks(max) }}
                        gridXValues={integerTicks(max)}
                        theme={CHART_THEME}
                      />
                    )}
                  </S.ChartBody>
                </S.ChartCard>
              )
            })}
          </S.StatRowThree>
        )}
      </S.SectionBlock>
    </S.Container>
  )
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

export default SectionDashboard
